import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import FeedbackWidget from './beta/FeedbackWidget';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { useRoles } from '../context/RolesContext';
import { useLanguage } from '../context/LanguageContext';
import Tooltip from './ui/Tooltip';
import useSystemStatus from '../hooks/useSystemStatus';
import { useWmStaff } from '../context/WmStaffContext';

// ── Nav groups definition ──────────────────────────────────────────────────────
// Each group: { key, label, icon, items[] }
// Each item:  { key, label, tk?, featureGate? }

const NAV_GROUPS = [
  // ── 1. Dashboard ────────────────────────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
      </svg>
    ),
    items: [{ key: 'dashboard', label: 'Dashboard', tk: 'nav.dashboard' }],
    singleItem: true,
  },

  // ── 2. Leads (promoted to top-level) ────────────────────────────────────────
  {
    key: 'leads',
    label: 'Leads',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
      </svg>
    ),
    items: [{ key: 'leadhub', label: 'Leads', tk: 'nav.leadhub' }],
    singleItem: true,
  },

  // ── 3. Sales ─────────────────────────────────────────────────────────────────
  {
    key: 'sales',
    label: 'Sales',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    items: [
      { key: 'estimates',     label: 'Estimates',     tk: 'nav.estimates' },
      { key: 'invoices',      label: 'Invoices',      tk: 'nav.invoices',      featureGate: 'invoicesEnabled' },
      { key: 'client-portal', label: 'Client Portal', tk: 'nav.clientPortal',  featureGate: 'clientPortalEnabled' },
    ],
  },

  // ── 4. Operations ────────────────────────────────────────────────────────────
  {
    key: 'operations',
    label: 'Operations',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
    items: [
      { key: 'workflow',        label: 'Workflow',         tk: 'nav.workflow',        featureGate: 'workflowEnabled' },
      { key: 'orders',          label: 'Work Orders',      tk: 'nav.orders' },
      { key: 'inventory',       label: 'Inventory',        tk: 'nav.inventory' },
      { key: 'scheduling',      label: 'Scheduling',       tk: 'nav.scheduling',      badge: 'scheduling' },
      { key: 'subcontractors',  label: 'Subcontractors',   tk: 'nav.subcontractors' },
      { key: 'parcel-tracking', label: 'Parcel Tracking',  tk: 'nav.parcelTracking' },
    ],
  },

  // ── 5. Catalog (Customers + Vehicles + Manufacturers) ───────────────────────
  {
    key: 'catalog',
    label: 'Catalog',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
      </svg>
    ),
    items: [
      { key: 'lists-customers',      label: 'Customers',      tk: 'nav.customers' },
      { key: 'lists-vehicles',       label: 'Vehicles',       tk: 'nav.vehicles' },
      { key: 'lists-manufacturers',  label: 'Manufacturers',  tk: 'nav.manufacturers' },
    ],
  },

  // ── 7. Marketing (gated; Performance + Intelligence removed) ─────────────────
  {
    key: 'marketing',
    label: 'Marketing',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
      </svg>
    ),
    featureGate: 'marketingEnabled',
    singleItem: true,
    items: [
      { key: 'marketing', label: 'Marketing', tk: 'nav.marketing', featureGate: 'marketingEnabled' },
    ],
  },

  // ── 8. Team (Performance; TODO: add Users and Leaderboards) ──────────────────
  {
    key: 'team',
    label: 'Team',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
    // TODO: add Users and Leaderboards when those views are ready
    singleItem: true,
    items: [
      { key: 'performance', label: 'Performance', tk: 'nav.performance', featureGate: 'xpEnabled' },
    ],
  },

  // ── 9. Reports ───────────────────────────────────────────────────────────────
  {
    key: 'reports',
    label: 'Reports',
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
    featureGate: 'reportsEnabled',
    singleItem: true,
    items: [{ key: 'reports', label: 'Reports', tk: 'nav.reports', featureGate: 'reportsEnabled' }],
  },
];

// Simple mode: only show these item keys
const SIMPLE_MODE_KEYS = new Set(['dashboard', 'estimates', 'leadhub', 'invoices', 'lists-customers']);

// ── Status LED + hover histogram popover ─────────────────────────────────────

function StatusLed({ status, checking }) {
  const color = {
    operational: { bg: '#22c55e', shadow: 'rgba(34,197,94,0.6)', pulse: false },
    degraded:    { bg: '#f59e0b', shadow: 'rgba(245,158,11,0.6)', pulse: true  },
    outage:      { bg: '#ef4444', shadow: 'rgba(239,68,68,0.6)',  pulse: true  },
  }[status] ?? { bg: '#6b7280', shadow: 'rgba(107,114,128,0.4)', pulse: false };

  if (checking) {
    return <span className="w-2 h-2 rounded-full bg-gray-400 opacity-40 flex-shrink-0" />;
  }

  return (
    <span className="relative flex items-center justify-center w-3 h-3 flex-shrink-0">
      {color.pulse && (
        <span className="absolute w-3 h-3 rounded-full opacity-50 animate-ping" style={{ backgroundColor: color.bg }} />
      )}
      <span className="relative w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color.bg, boxShadow: `0 0 6px 2px ${color.shadow}` }} />
    </span>
  );
}

const STATUS_LABEL = { operational: 'OK', degraded: 'Degraded', outage: 'Outage' };
const STATUS_COLOR = { operational: '#22c55e', degraded: '#f59e0b', outage: '#ef4444' };
const SERVICE_DOT  = { operational: '#22c55e', degraded: '#f59e0b', outage: '#ef4444' };

// Deterministic pseudo-random for histogram mock data
function seededRand(seed) {
  const s = (seed * 1664525 + 1013904223) & 0x7fffffff;
  return s / 0x7fffffff;
}
function generateUptimeHistory(days = 45) {
  const dayMs = 24 * 60 * 60 * 1000;
  const today = Math.floor(Date.now() / dayMs);
  return Array.from({ length: days }, (_, i) => {
    const daySeed = today - (days - 1 - i);
    const r = seededRand(daySeed);
    const status = r < 0.02 ? 'outage' : r < 0.06 ? 'degraded' : 'operational';
    const uptime = status === 'operational' ? 99 + seededRand(daySeed + 1) * 1
                 : status === 'degraded'    ? 90 + seededRand(daySeed + 2) * 8
                 :                            70 + seededRand(daySeed + 3) * 20;
    const date = new Date((today - (days - 1 - i)) * dayMs);
    return { status, uptime: Math.min(100, uptime), date };
  });
}

function StatusPopover({ status, services, incidents, uptime, lastChecked, checking, error, refresh, onMouseEnter, onMouseLeave, positionStyle }) {
  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  const history = generateUptimeHistory(45);

  return (
    <div
      className="w-[318px] rounded-xl border shadow-2xl overflow-hidden"
      style={{
        position: 'fixed',
        zIndex: 9999,
        background: 'var(--wm-nav-bg)',
        borderColor: 'var(--wm-nav-border)',
        ...positionStyle,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-2.5 border-b" style={{ borderColor: 'var(--wm-nav-border)' }}>
        <StatusLed status={status} checking={checking} />
        <div className="flex-1 min-w-0">
          <p className="text-[var(--wm-text-ui)] font-semibold" style={{ color: 'var(--wm-nav-text-active)' }}>WrapMind Status</p>
          <p className="text-[var(--wm-text-caption)]" style={{ color: STATUS_COLOR[status] ?? '#6b7280' }}>
            {checking ? 'Checking…' : error ? 'Unknown' : STATUS_LABEL[status]}
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={checking}
          className="w-6 h-6 flex items-center justify-center rounded transition-colors hover:opacity-100 disabled:opacity-40"
          style={{ color: 'var(--wm-nav-text)' }}
          title="Refresh"
        >
          <svg className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        </button>
      </div>

      {/* 45-day uptime histogram */}
      <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: 'var(--wm-nav-border)' }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[var(--wm-text-micro)] font-semibold uppercase tracking-widest opacity-50" style={{ color: 'var(--wm-nav-text)' }}>
            45-day uptime
          </span>
          <span className="text-[var(--wm-text-meta)] font-bold" style={{ color: 'var(--wm-nav-accent)' }}>
            {uptime}
          </span>
        </div>
        <div className="flex items-end gap-px" style={{ height: 32 }}>
          {history.map((day, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm transition-opacity hover:opacity-100"
              style={{
                height: `${Math.max(4, (day.uptime / 100) * 32)}px`,
                backgroundColor:
                  day.status === 'operational' ? 'var(--wm-nav-accent)'
                  : day.status === 'degraded'  ? '#f59e0b'
                  :                              '#ef4444',
                opacity: 0.75,
              }}
              title={`${day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${day.uptime.toFixed(2)}%`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[var(--wm-text-micro)] opacity-30" style={{ color: 'var(--wm-nav-text)' }}>45 days ago</span>
          <span className="text-[var(--wm-text-micro)] opacity-30" style={{ color: 'var(--wm-nav-text)' }}>Today</span>
        </div>
      </div>

      {/* Active incidents */}
      {incidents.length > 0 && (
        <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--wm-nav-border)', background: 'rgba(239,68,68,0.05)' }}>
          {incidents.map(inc => (
            <div key={inc.id} className="flex gap-2">
              <span className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLOR[inc.severity] }} />
              <div>
                <p className="text-[var(--wm-text-meta)] font-semibold" style={{ color: STATUS_COLOR[inc.severity] }}>{inc.title}</p>
                <p className="text-[var(--wm-text-caption)] leading-relaxed" style={{ color: 'var(--wm-nav-text)' }}>{inc.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Services list */}
      <div className="px-4 py-2 space-y-1">
        <p className="text-[var(--wm-text-micro)] font-semibold uppercase tracking-widest mb-2 opacity-50" style={{ color: 'var(--wm-nav-text)' }}>Services</p>
        {services.map(svc => (
          <div key={svc.id} className="flex items-center gap-2 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: SERVICE_DOT[svc.status] }} />
            <span className="flex-1 text-[var(--wm-text-meta)]" style={{ color: 'var(--wm-nav-text-active)' }}>{svc.name}</span>
            <span className="text-[var(--wm-text-caption)] font-medium" style={{ color: STATUS_COLOR[svc.status] }}>{STATUS_LABEL[svc.status]}</span>
            {svc.latencyMs > 0 && (
              <span className="text-[var(--wm-text-micro)] font-mono opacity-40" style={{ color: 'var(--wm-nav-text)' }}>{svc.latencyMs}ms</span>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--wm-nav-border)' }}>
        <span className="text-[var(--wm-text-micro)] opacity-40" style={{ color: 'var(--wm-nav-text)' }}>
          Checked {fmtTime(lastChecked)}
        </span>
        <div className="flex items-center gap-1.5 text-[var(--wm-text-micro)] opacity-40" style={{ color: 'var(--wm-nav-text)' }}>
          <span style={{ color: 'var(--wm-nav-accent)' }}>●</span> OK
          <span style={{ color: '#f59e0b' }}>●</span> Degraded
          <span style={{ color: '#ef4444' }}>●</span> Outage
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar greeting module ──────────────────────────────────────────────────

function getGreeting(firstName) {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return <span>Good morning, {firstName} ☀️  Here's <strong>what's happening</strong> at the shop <em>today</em>.</span>;
  if (h >= 12 && h < 17) return <span>Good afternoon, {firstName} 👋  Here's <strong>what's happening</strong> at the shop <em>today</em>.</span>;
  if (h >= 17 && h < 21) return <span>Good evening, {firstName} 🌙  Here's <strong>what's happening</strong> at the shop <em>today</em>.</span>;
  return <span>Burning the midnight oil, {firstName} 🔥  Here's <em>what's on deck</em>.</span>;
}

function getTodayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function NavGreeting({ collapsed, onNavigate }) {
  const [firstName, setFirstName] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
      const shop = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
      const full = user.displayName || user.name || shop.name || '';
      return full.split(' ')[0] || 'there';
    } catch { return 'there'; }
  });

  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('wm-nav-greeting-expanded');
      return saved === null ? true : saved === 'true';
    } catch { return true; }
  });

  useEffect(() => {
    const sync = () => {
      try {
        const user = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
        const shop = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
        const full = user.displayName || user.name || shop.name || '';
        setFirstName(full.split(' ')[0] || 'there');
      } catch {}
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const toggle = () => {
    setExpanded(v => {
      const next = !v;
      try { localStorage.setItem('wm-nav-greeting-expanded', String(next)); } catch {}
      return next;
    });
  };

  const shopData = useMemo(() => {
    try {
      const todayStr = getTodayStr();
      const now = new Date();
      const currentTimeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;

      // 1. Next appointment today
      const appointments = JSON.parse(localStorage.getItem('wm-scheduling-v1') || '[]');
      const todayAppts = appointments
        .filter(a => a.date === todayStr && a.status !== 'cancelled')
        .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
      const nextAppt = todayAppts.find(a => (a.time || '') > currentTimeStr) || null;

      // 2. Expiring estimates (sent > 7 days ago)
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const estimates = JSON.parse(localStorage.getItem('wm-estimates-v2') || '[]');
      const expiringCount = estimates.filter(e => {
        if (e.status !== 'sent') return false;
        if (!e.sentAt) return false;
        return new Date(e.sentAt) < sevenDaysAgo;
      }).length;

      // 3. Outstanding balance
      const invoices = JSON.parse(localStorage.getItem('wm-invoices-v1') || '[]');
      const outstandingBalance = invoices
        .filter(inv => inv.status !== 'paid' && inv.status !== 'void')
        .reduce((sum, inv) => sum + (Number(inv.amountDue) || 0), 0);

      // 4. Active work orders
      const workOrders = JSON.parse(localStorage.getItem('wm-work-orders-v1') || '[]');
      const activeWoCount = workOrders.filter(
        wo => wo.status === 'active' || wo.status === 'in_progress' || wo.status === 'open'
      ).length;

      return { nextAppt, expiringCount, outstandingBalance, activeWoCount };
    } catch {
      return { nextAppt: null, expiringCount: 0, outstandingBalance: 0, activeWoCount: 0 };
    }
  }, []);

  if (collapsed) return null;

  return (
    <div className="flex-shrink-0 px-2 pb-2">
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: '1px solid var(--wm-nav-border)', background: 'rgba(125,147,174,0.06)' }}
      >
        {/* Header — always visible, click to toggle */}
        <button
          onClick={toggle}
          className="w-full flex items-center justify-between px-2.5 py-1.5 text-left hover:opacity-80 transition-opacity"
        >
          <span className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest" style={{ color: 'var(--wm-nav-text)' }}>
            Today
          </span>
          <svg
            className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`}
            style={{ color: 'var(--wm-nav-text)' }}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expandable body */}
        {expanded && (
          <div className="px-2.5 pb-2.5 -mt-0.5">
            <p className="text-[var(--wm-text-meta)] leading-relaxed" style={{ color: 'var(--wm-nav-text)' }}>
              {getGreeting(firstName)}
            </p>

            {/* 1. Next appointment today */}
            {shopData.nextAppt ? (
              <div
                className="flex items-center gap-1.5 text-[var(--wm-text-meta)] cursor-pointer hover:opacity-80 transition-opacity mt-1.5"
                style={{ color: 'var(--wm-nav-text)' }}
                onClick={() => onNavigate && onNavigate('scheduling')}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m5-8a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{shopData.nextAppt.time} · {shopData.nextAppt.customerName}</span>
              </div>
            ) : (
              <div
                className="flex items-center gap-1.5 text-[var(--wm-text-meta)] mt-1.5 opacity-60"
                style={{ color: 'var(--wm-nav-text)' }}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m5-8a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>No appointments <em>today</em></span>
              </div>
            )}

            {/* 2. Expiring estimates (only shown when count > 0) */}
            {shopData.expiringCount > 0 && (
              <div
                className="flex items-center gap-1.5 text-[var(--wm-text-meta)] cursor-pointer hover:opacity-80 transition-opacity mt-1.5"
                style={{ color: 'var(--wm-nav-text)' }}
                onClick={() => onNavigate && onNavigate('estimates')}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span><strong>{shopData.expiringCount}</strong> estimate{shopData.expiringCount > 1 ? 's' : ''} <strong>awaiting reply</strong></span>
              </div>
            )}

            {/* 3. Outstanding balance */}
            {shopData.outstandingBalance > 0 ? (
              <div
                className="flex items-center gap-1.5 text-[var(--wm-text-meta)] cursor-pointer hover:opacity-80 transition-opacity mt-1.5"
                style={{ color: 'var(--wm-nav-text)' }}
                onClick={() => onNavigate && onNavigate('invoices')}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span><strong>${shopData.outstandingBalance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong> <em>outstanding</em></span>
              </div>
            ) : (
              <div
                className="flex items-center gap-1.5 text-[var(--wm-text-meta)] mt-1.5 opacity-60"
                style={{ color: 'var(--wm-nav-text)' }}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span><strong>All</strong> invoices settled</span>
              </div>
            )}

            {/* 4. Active work orders (only shown when count > 0) */}
            {shopData.activeWoCount > 0 && (
              <div
                className="flex items-center gap-1.5 text-[var(--wm-text-meta)] cursor-pointer hover:opacity-80 transition-opacity mt-1.5"
                style={{ color: 'var(--wm-nav-text)' }}
                onClick={() => onNavigate && onNavigate('orders')}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-6.613m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
                </svg>
                <span><strong>{shopData.activeWoCount}</strong> <em>active</em> work order{shopData.activeWoCount > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SideNav({ currentView, onNavigate }) {
  const { xpEnabled, workflowEnabled, invoicesEnabled, reportsEnabled, clientPortalEnabled, marketingEnabled, simpleMode } = useFeatureFlags();
  const { isWmStaff, wmCan } = useWmStaff();
  const { currentRole } = useRoles();
  const { t } = useLanguage();
  const systemStatus = useSystemStatus();

  const todayAppts = useMemo(() => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const raw = JSON.parse(localStorage.getItem('wm-scheduling-v1') || '[]');
      return raw.filter(a => a.date === today && a.status !== 'cancelled').length;
    } catch { return 0; }
  }, []);

  const isAdmin = currentRole === 'superadmin' || currentRole === 'admin';

  // Determine which group is active based on currentView
  function getActiveGroupKey(view) {
    for (const group of NAV_GROUPS) {
      if (group.items.some(item => item.key === view)) return group.key;
    }
    // Admin views
    if (['intelligence', 'shops', 'audit-log', 'site-bugs'].includes(view)) return '__admin__';
    // Staff views
    if (['staff-portal', 'my-accounts', 'staff-settings'].includes(view)) return '__staff__';
    return null;
  }

  const activeGroupKey = getActiveGroupKey(currentView);
  // manualExpanded: group key the user explicitly toggled; null means follow activeGroupKey
  const [manualExpanded, setManualExpanded] = useState(/** @type {string|null} */ (null));
  // When currentView changes, clear manual override so the active group is shown
  const [prevView, setPrevView] = useState(currentView);
  const expandedGroup = currentView !== prevView
    ? activeGroupKey
    : (manualExpanded !== null ? manualExpanded : activeGroupKey);
  function setExpandedGroup(key) {
    if (currentView !== prevView) setPrevView(currentView);
    setManualExpanded(key);
  }

  const [statusOpen, setStatusOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const ledRef = useRef(null);

  const openPopover = () => {
    clearTimeout(hoverTimeoutRef.current);
    if (ledRef.current && ledRef.current.isConnected) {
      const r = ledRef.current.getBoundingClientRect();
      const panelH = 420;
      const top = Math.min(r.top, window.innerHeight - panelH - 8);
      setPopoverPos({ left: r.right + 8, top });
    }
    setStatusOpen(true);
  };
  const closePopover = () => {
    hoverTimeoutRef.current = setTimeout(() => setStatusOpen(false), 200);
  };

  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wm-nav-favorites') || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem('wm-nav-favorites', JSON.stringify(favorites));
  }, [favorites]);

  function toggleFavorite(key, label, e) {
    e.stopPropagation();
    setFavorites(prev =>
      prev.some(f => f.key === key)
        ? prev.filter(f => f.key !== key)
        : [...prev, { key, label }]
    );
  }
  function isFavorite(key) { return favorites.some(f => f.key === key); }

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('wm-nav-collapsed') === 'true';
  });

  const [staffExpanded, setStaffExpanded] = useState(false);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('wm-nav-collapsed', String(next));
  };

  // Feature gate check for a group or item
  const featureFlags = { xpEnabled, workflowEnabled, invoicesEnabled, reportsEnabled, clientPortalEnabled, marketingEnabled };

  function isGateEnabled(gate) {
    if (!gate) return true;
    return !!featureFlags[gate];
  }

  // Filter visible items within a group
  function visibleItems(group) {
    return group.items.filter(item => {
      if (simpleMode && !SIMPLE_MODE_KEYS.has(item.key)) return false;
      if (item.confidential && !isAdmin) return false;
      if (!isGateEnabled(item.featureGate)) return false;
      return true;
    });
  }

  // Should we show a group at all?
  function shouldShowGroup(group) {
    // If the group itself is gated, check that gate
    if (group.featureGate && !isGateEnabled(group.featureGate)) {
      // still show if any item is gated differently — just check visible items
    }
    const items = visibleItems(group);
    if (items.length === 0) return false;
    return true;
  }

  function handleGroupClick(group) {
    if (collapsed) {
      // In collapsed mode, click goes to first visible item
      const items = visibleItems(group);
      if (items.length > 0) {
        const item = items[0];
        if (item.confidential && !isWmStaff) return;
        onNavigate(item.key);
      }
      return;
    }
    if (group.singleItem) {
      const items = visibleItems(group);
      if (items.length > 0) {
        const item = items[0];
        if (item.confidential && !isWmStaff) return;
        onNavigate(item.key);
      }
      return;
    }
    // Accordion toggle
    setExpandedGroup(prev => prev === group.key ? null : group.key);
  }

  function handleItemClick(item) {
    if (item.confidential && !isWmStaff) return;
    onNavigate(item.key);
  }

  function isGroupActive(group) {
    return group.items.some(item => item.key === currentView);
  }

  return (
    <aside
      className={`hidden md:flex md:flex-col md:border-r md:flex-shrink-0 md:h-screen md:sticky md:top-0 transition-all duration-200 relative ${collapsed ? 'md:w-14' : 'md:w-[180px]'}`}
      style={{ background: 'color-mix(in srgb, var(--wm-nav-bg) 62%, transparent)', borderColor: 'var(--wm-glass-border)', backdropFilter: 'blur(32px) saturate(160%)', WebkitBackdropFilter: 'blur(32px) saturate(160%)', boxShadow: 'inset -1px 0 0 var(--wm-glass-specular)', zIndex: 10 }}
    >
      {/* ── Floating shadow — gradient div that bleeds past the right edge ──── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 h-full"
        style={{
          width: '64px',
          transform: 'translateX(100%)',
          background: 'linear-gradient(to right, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.07) 40%, transparent 100%)',
          zIndex: 10,
        }}
      />

      {/* ── Logo row ──────────────────────────────────────────────────────────── */}
      <div
        className={`h-[58px] flex items-center flex-shrink-0 border-b relative ${collapsed ? 'justify-center px-0' : 'px-3'}`}
        style={{ borderColor: 'var(--wm-nav-border)' }}
      >
        {!collapsed && (
          <>
            {/* WrapMind brand logo */}
            <button
              onClick={() => onNavigate('dashboard')}
              className="flex-1 text-left transition-opacity hover:opacity-80 flex items-center"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              {/* White logo — dark mode only */}
              <img
                src="/logo-dark.png"
                alt="WrapMind"
                className="hidden dark:block"
                style={{ height: '28px', width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
              />
              {/* Black logo — light mode only */}
              <img
                src="/logo-light.png"
                alt="WrapMind"
                className="block dark:hidden"
                style={{ height: '28px', width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
              />
            </button>

            {/* Status LED */}
            <div
              ref={ledRef}
              className="flex-shrink-0"
              onMouseEnter={openPopover}
              onMouseLeave={closePopover}
            >
              <Tooltip text={`System ${STATUS_LABEL[systemStatus.status] ?? 'Status'}`} position="bottom">
                <button
                  className="flex items-center justify-center w-5 h-5 rounded flex-shrink-0 transition-opacity hover:opacity-100 relative top-[-2px]"
                  style={{ opacity: 0.85 }}
                >
                  <StatusLed status={systemStatus.status} checking={systemStatus.checking} />
                </button>
              </Tooltip>

              {statusOpen && popoverPos && createPortal(
                <StatusPopover
                  {...systemStatus}
                  positionStyle={popoverPos}
                  onMouseEnter={() => clearTimeout(hoverTimeoutRef.current)}
                  onMouseLeave={closePopover}
                />,
                document.body
              )}
            </div>
          </>
        )}

        {/* Collapsed: status LED */}
        {collapsed && (
          <div
            ref={ledRef}
            className="absolute bottom-[10px] right-1.5"
            onMouseEnter={openPopover}
            onMouseLeave={closePopover}
          >
            <StatusLed status={systemStatus.status} checking={systemStatus.checking} />
            {statusOpen && popoverPos && createPortal(
              <StatusPopover
                {...systemStatus}
                positionStyle={popoverPos}
                onMouseEnter={() => clearTimeout(hoverTimeoutRef.current)}
                onMouseLeave={closePopover}
              />,
              document.body
            )}
          </div>
        )}

        <Tooltip text={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} position="right">
          <button
            onClick={toggleCollapsed}
            className={`flex items-center justify-center w-6 h-6 rounded flex-shrink-0 wm-nav-item ${collapsed ? '' : 'ml-1'}`}
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${collapsed ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </Tooltip>
      </div>

      {/* ── New Estimate CTA ─────────────────────────────────────────────────── */}
      <div className={`pt-3 pb-3 flex-shrink-0 ${collapsed ? 'px-2' : 'px-2.5'}`}>
        <Tooltip text={collapsed ? 'New Estimate (⌘K)' : undefined} position="right" className="w-full">
          <button
            onClick={() => onNavigate('estimate')}
            className="w-full flex items-center justify-center gap-2 h-10 text-white font-bold transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--wm-nav-accent)', borderRadius: 'var(--wm-radius-md, 8px)', fontSize: 'var(--wm-text-cta)' }}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            {!collapsed && <span>{t('action.newEstimate')}</span>}
          </button>
        </Tooltip>

      </div>

      {/* ── Nav groups (accordion) ────────────────────────────────────────────── */}
      <nav className="px-2 pt-[14px] pb-1 space-y-0.5 flex-shrink-0 overflow-y-auto flex-1">
        {/* ── Pinned / Favorites section ───────────────────────────────────────── */}
        {favorites.length > 0 && !collapsed && (
          <div>
            <div className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest px-2 pb-1" style={{ color: 'var(--wm-nav-text)' }}>
              Pinned
            </div>
            {favorites.map(fav => (
              <div key={fav.key} className="group">
                <button
                  onClick={() => onNavigate(fav.key)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] wm-nav-item text-left ${currentView === fav.key ? 'wm-nav-active' : ''}`}
                >
                  <span className="flex-shrink-0 text-[var(--wm-text-caption)]" style={{ color: '#F59E0B' }}>★</span>
                  <span className="flex-1 truncate font-medium">{fav.label}</span>
                  <button
                    onClick={(e) => toggleFavorite(fav.key, fav.label, e)}
                    className="text-[var(--wm-text-caption)] opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: '#7D93AE' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#7D93AE'; }}
                    aria-label="Remove from favorites"
                  >
                    ×
                  </button>
                </button>
              </div>
            ))}
            <div className="my-1.5 border-t" style={{ borderColor: 'var(--wm-nav-border)' }} />
          </div>
        )}

        {NAV_GROUPS.filter(shouldShowGroup).map((group) => {
          const items = visibleItems(group);
          const isExpanded = expandedGroup === group.key;
          const groupIsActive = isGroupActive(group);

          return (
            <div key={group.key}>
              {/* Group header button */}
              <div className="relative group">
              <Tooltip text={collapsed ? group.label : undefined} position="right" className="w-full">
                <button
                  onClick={() => handleGroupClick(group)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] wm-nav-item
                    ${collapsed ? 'justify-center' : 'text-left'}
                    ${groupIsActive ? 'wm-nav-active' : ''}`}
                >
                  {group.icon}
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate font-medium">{group.label}</span>
                      {/* Star button for single-item groups */}
                      {group.singleItem && (
                        <button
                          onClick={(e) => toggleFavorite(group.items[0].key, group.label, e)}
                          aria-label={isFavorite(group.items[0].key) ? 'Remove from favorites' : 'Add to favorites'}
                          className={`flex-shrink-0 text-[var(--wm-text-caption)] transition-opacity ${isFavorite(group.items[0].key) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                          style={{ color: isFavorite(group.items[0].key) ? '#F59E0B' : 'var(--wm-nav-text)' }}
                        >
                          {isFavorite(group.items[0].key) ? '★' : '☆'}
                        </button>
                      )}
                      {/* Scheduling badge on the Operations group */}
                      {group.key === 'operations' && todayAppts > 0 && (
                        <span className="ml-auto min-w-[18px] h-[18px] rounded-full bg-[var(--accent-primary)] text-white text-[var(--wm-text-caption)] font-bold flex items-center justify-center px-1">
                          {todayAppts}
                        </span>
                      )}
                      {/* Chevron for multi-item groups */}
                      {!group.singleItem && (
                        <svg
                          className={`w-3 h-3 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          style={{ color: 'var(--wm-nav-text)' }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </>
                  )}
                </button>
              </Tooltip>
              </div>

              {/* Sub-items */}
              {!collapsed && !group.singleItem && isExpanded && items.length > 0 && (
                <div className="ml-3 mt-0.5 space-y-0.5 pl-2" style={{ borderLeft: '1px solid var(--wm-nav-border)' }}>
                  {items.map(item => {
                    const itemLabel = item.tk ? t(item.tk) : item.label;
                    return (
                      <div key={item.key} className="group">
                        <button
                          onClick={() => handleItemClick(item)}
                          className={`w-full text-left px-2 py-1 rounded text-[var(--wm-text-ui)] wm-nav-item flex items-center gap-1.5
                            ${currentView === item.key ? 'wm-nav-active' : ''}`}
                        >
                          <span className="flex-1 truncate">{itemLabel}</span>
                          {/* Star button for sub-items */}
                          <button
                            onClick={(e) => toggleFavorite(item.key, itemLabel, e)}
                            aria-label={isFavorite(item.key) ? 'Remove from favorites' : 'Add to favorites'}
                            className={`flex-shrink-0 text-[var(--wm-text-caption)] transition-opacity ${isFavorite(item.key) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                            style={{ color: isFavorite(item.key) ? '#F59E0B' : 'var(--wm-nav-text)' }}
                          >
                            {isFavorite(item.key) ? '★' : '☆'}
                          </button>
                          {item.badge === 'scheduling' && todayAppts > 0 && (
                            <span className="min-w-[16px] h-4 rounded-full bg-[var(--accent-primary)] text-white text-[var(--wm-text-caption)] font-bold flex items-center justify-center px-1">
                              {todayAppts}
                            </span>
                          )}
                          {item.confidential && !isWmStaff && (
                            <svg className="w-3 h-3 flex-shrink-0 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Admin-only items (hidden when WrapMind Staff active — items live in Staff box) ── */}
        {isAdmin && !isWmStaff && (
          <div className="mt-1">
            <div className="my-1.5 border-t" style={{ borderColor: 'var(--wm-nav-border)' }} />

            {/* Confidential lock badge */}
            {!collapsed && !isWmStaff && (
              <div className="mx-1 mb-1.5 px-2 py-1 rounded flex items-center gap-1.5"
                style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.18)' }}>
                <svg className="w-3 h-3 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                <span className="text-[var(--wm-text-caption)] text-red-400 font-medium">Staff clearance required</span>
              </div>
            )}

            {/* ── Staff Insights category label ── */}
            {!collapsed && (
              <div className="px-2 pt-2 pb-0.5">
                <span className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest" style={{ color: 'var(--wm-nav-text-secondary, #7D93AE)' }}>
                  Staff Insights
                </span>
              </div>
            )}

            {[
              {
                view: 'intelligence',
                title: t('nav.intelligence'),
                icon: <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />,
                activeClass: 'wm-nav-active',
              },
              {
                view: 'shops',
                title: t('nav.shops'),
                icon: <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />,
                activeClass: 'wm-nav-active',
              },
              {
                view: 'site-bugs',
                title: 'Site Bugs',
                icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 001.152 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.953-.135 2.864-.386.207-.066.431-.99.431-1.62a3.75 3.75 0 00-7.5 0c0 .63.224 1.554.431 1.62A7.51 7.51 0 0012 8.25zm-4.5 2.25h.008v.008H7.5V10.5zm0 3h.008v.008H7.5V13.5zm0 3h.008v.008H7.5V16.5zm9-6h.008v.008H16.5V10.5zm0 3h.008v.008H16.5V13.5zm0 3h.008v.008H16.5V16.5z" />,
                activeClass: 'wm-nav-active',
              },
            ].map(({ view, title, icon, activeClass }) => (
              <Tooltip key={view} text={collapsed ? title : undefined} position="right" className="w-full">
                <button
                  onClick={() => isWmStaff && onNavigate(view)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] wm-nav-item
                    ${collapsed ? 'justify-center' : 'text-left'}
                    ${currentView === view ? activeClass : ''}
                    ${!isWmStaff ? 'opacity-60' : ''}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    {icon}
                  </svg>
                  {!collapsed && <span className="flex-1 truncate">{title}</span>}
                  {!collapsed && !isWmStaff && (
                    <svg className="w-3 h-3 flex-shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  )}
                </button>
              </Tooltip>
            ))}

            {/* ── Audit Log (standalone — operations tool, not grouped under Staff Insights) ── */}
            <Tooltip text={collapsed ? t('nav.auditLog') : undefined} position="right" className="w-full">
              <button
                onClick={() => isWmStaff && onNavigate('audit-log')}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] wm-nav-item
                  ${collapsed ? 'justify-center' : 'text-left'}
                  ${currentView === 'audit-log' ? 'wm-nav-active' : ''}
                  ${!isWmStaff ? 'opacity-60' : ''}`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
                {!collapsed && <span className="flex-1 truncate">{t('nav.auditLog')}</span>}
                {!collapsed && !isWmStaff && (
                  <svg className="w-3 h-3 flex-shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                )}
              </button>
            </Tooltip>
          </div>
        )}
      </nav>

      {/* ── WrapMind Staff Group ───────────────────────────────────────────── */}
      {isWmStaff && (
        <div className="mx-2 mb-2 flex-shrink-0">
          <div
            className="rounded-xl overflow-hidden bg-amber-50 border border-amber-200 dark:bg-amber-400/[0.06] dark:border-amber-500/[0.18]"
          >
            {/* Header — clickable to expand/collapse */}
            {!collapsed && (
              <button
                onClick={() => setStaffExpanded(p => !p)}
                className={`w-full px-3 py-1.5 flex items-center gap-1.5 transition-opacity hover:opacity-80 ${staffExpanded ? 'border-b border-amber-200 dark:border-amber-500/[0.12]' : ''}`}
              >
                <svg className="w-3 h-3 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                </svg>
                <span className="flex-1 text-left text-[var(--wm-text-caption)] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">WM STAFF</span>
                <svg
                  className={`w-3 h-3 text-amber-600 dark:text-amber-400 flex-shrink-0 transition-transform ${staffExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
            {/* Nav items — only shown when expanded */}
            {staffExpanded && <div className="p-1 space-y-0.5">
              {/* Shop Portal / My Accounts */}
              {wmCan('org.enter') && (
                <button
                  onClick={() => onNavigate(wmCan('internal.shop_profiles') ? 'staff-portal' : 'my-accounts')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] rounded-lg transition-colors text-left
                    ${currentView === 'staff-portal' || currentView === 'my-accounts'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                      : 'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-400/10 dark:hover:text-amber-200'}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                  </svg>
                  {!collapsed && <span className="truncate">{wmCan('internal.shop_profiles') ? 'Shop Portal' : 'My Accounts'}</span>}
                </button>
              )}
              {/* Intelligence — Senior + Support */}
              {wmCan('internal.intelligence') && (
                <button
                  onClick={() => onNavigate('intelligence')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] rounded-lg transition-colors text-left
                    ${currentView === 'intelligence'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                      : 'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-400/10 dark:hover:text-amber-200'}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  {!collapsed && <span className="truncate">Intelligence</span>}
                </button>
              )}
              {/* Shop Profiles — Senior + Support */}
              {wmCan('internal.shop_profiles') && (
                <button
                  onClick={() => onNavigate('shops')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] rounded-lg transition-colors text-left
                    ${currentView === 'shops'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                      : 'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-400/10 dark:hover:text-amber-200'}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                  </svg>
                  {!collapsed && <span className="truncate">Shop Profiles</span>}
                </button>
              )}
              {/* Site Bugs — Senior + Support */}
              {wmCan('internal.bugs') && (
                <button
                  onClick={() => onNavigate('site-bugs')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] rounded-lg transition-colors text-left
                    ${currentView === 'site-bugs'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                      : 'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-400/10 dark:hover:text-amber-200'}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 001.152 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.77 3.77 0 01.4-2.25m0 0a3.728 3.728 0 013.48 0m-3.48 0A3.745 3.745 0 0112 3.75c.97 0 1.897.201 2.74.555m0 0 .28 1.696" />
                  </svg>
                  {!collapsed && <span className="truncate">Site Bugs</span>}
                </button>
              )}
              {/* Audit Log — Senior + Support */}
              {wmCan('internal.audit') && (
                <button
                  onClick={() => onNavigate('audit-log')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] rounded-lg transition-colors text-left
                    ${currentView === 'audit-log'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                      : 'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-400/10 dark:hover:text-amber-200'}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                  </svg>
                  {!collapsed && <span className="truncate">Audit Log</span>}
                </button>
              )}
              {/* Staff Settings — Senior only */}
              {wmCan('internal.staff_manage') && (
                <button
                  onClick={() => onNavigate('staff-settings')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-[var(--wm-text-ui)] rounded-lg transition-colors text-left
                    ${currentView === 'staff-settings'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                      : 'text-amber-700 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-400/10 dark:hover:text-amber-200'}`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                  {!collapsed && <span className="truncate">Staff Settings</span>}
                </button>
              )}
            </div>}
          </div>
        </div>
      )}

      {/* ── Greeting module ───────────────────────────────────────────────────── */}
      <NavGreeting collapsed={collapsed} onNavigate={onNavigate} />

      {/* ── Beta Feedback Widget ─────────────────────────────────────────────── */}
      <div className="pb-2 flex-shrink-0">
        <FeedbackWidget currentPage={currentView} collapsed={collapsed} />
      </div>

      {/* ── Settings + Help (pinned bottom) ────────────────────────────────── */}
      <div
        className={`px-2 py-2 flex-shrink-0 flex gap-1 ${collapsed ? 'flex-col' : 'items-center'}`}
        style={{ borderTop: '1px solid var(--wm-nav-border)' }}
      >
        <Tooltip text="Settings" position="top" className="w-full">
          <button
            onClick={() => onNavigate('settings')}
            className={`wm-settings-btn w-full flex items-center justify-center px-2 py-1.5 text-[var(--wm-text-ui)] wm-nav-item
              ${currentView === 'settings' ? 'wm-nav-active' : ''}`}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.43l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </Tooltip>
        <Tooltip text="Help" position="top" className="w-full">
          <button
            onClick={() => onNavigate('help')}
            className={`wm-settings-btn w-full flex items-center justify-center px-2 py-1.5 text-[var(--wm-text-ui)] wm-nav-item
              ${currentView === 'help' ? 'wm-nav-active' : ''}`}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
          </button>
        </Tooltip>
        {/* Sign-out lives in the Account dropdown (top bar) */}
      </div>
    </aside>
  );
}
